import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ---- Value Conversion Utilities ----

// Threshold: linear amplitude [0,1] → dB display
function formatThresh(n) {
  if (n <= 0) return '-∞ dB';
  const db = 20 * Math.log10(Math.max(1e-9, n));
  return (db >= 0 ? '+' : '') + db.toFixed(1) + ' dB';
}

// Time: normalized [0,1] → display string
const ATTACK_MAX_MS = 300;    // 0–300 ms
const HOLD_MAX_MS = 2000;     // 0–2000 ms
const RELEASE_MAX_MS = 2000;  // 0–2000 ms

function normToMs(n, maxMs) { return n * maxMs; }

function formatMs(ms) {
  if (ms >= 1000) return (ms / 1000).toFixed(2).replace(/\.?0+$/, '') + ' s';
  if (ms < 1) return '< 1 ms';
  return Math.round(ms) + ' ms';
}

// Depth: normalized [0,1] → [0, −80] dB
function normToDepthDb(n) { return -Math.max(0, Math.min(1, n)) * 80; }
function formatDepth(n) {
  if (n <= 0) return '0 dB';
  return normToDepthDb(n).toFixed(0) + ' dB';
}

// ---- Gate Envelope Canvas ----
function GateCanvas({ params }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#0d0d0d';
    ctx.fillRect(0, 0, W, H);

    const margin = { top: 22, right: 20, bottom: 24, left: 38 };
    const plotW = W - margin.left - margin.right;
    const plotH = H - margin.top - margin.bottom;

    const gainMin = -80;
    const gainMax = 0;

    const clampGain = (g) => Math.max(gainMin, Math.min(gainMax, g));
    const gainToY = (g) => margin.top + plotH * (1 - (clampGain(g) - gainMin) / (gainMax - gainMin));
    const timeToX = (t) => margin.left + Math.max(0, Math.min(1, t)) * plotW;

    // Plot background
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(margin.left, margin.top, plotW, plotH);

    // Horizontal grid lines (dB markers)
    const dbMarkers = [-60, -40, -20, 0];
    dbMarkers.forEach((db) => {
      const y = gainToY(db);
      ctx.strokeStyle = db === 0 ? '#282828' : '#181818';
      ctx.lineWidth = db === 0 ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(margin.left, y); ctx.lineTo(margin.left + plotW, y); ctx.stroke();
      ctx.fillStyle = db === 0 ? '#555' : '#333';
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText((db === 0 ? '0' : db) + 'dB', margin.left - 3, y + 3);
    });

    const bypass = params.bypass;
    const depthDb = bypass ? 0 : normToDepthDb(params.depth);
    const openY = gainToY(0);
    const depthY = gainToY(depthDb);

    if (bypass) {
      // Dashed flat 0 dB line when bypassed
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(timeToX(0), openY);
      ctx.lineTo(timeToX(1), openY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#555';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('BYPASS', timeToX(0.5), margin.top + plotH / 2);
      return;
    }

    // Build proportional timeline segments for visualization
    const atk = Math.max(0, params.attack);
    const hld = Math.max(0, params.hold);
    const rel = Math.max(0, params.release);

    const seg = {
      closedPre: 25,
      attack: Math.max(3, atk * 22),
      open: 30,
      hold: Math.max(3, hld * 22),
      release: Math.max(5, rel * 28),
      closedPost: 20,
    };
    const total = Object.values(seg).reduce((a, b) => a + b, 0);
    const toT = (s) => s / total;

    const t0 = 0;
    const t1 = toT(seg.closedPre);                                  // start of attack
    const t2 = t1 + toT(seg.attack);                               // end of attack / gate open
    const t3 = t2 + toT(seg.open);                                  // end of open / start of hold
    const t4 = t3 + toT(seg.hold);                                  // end of hold / start of release
    const t5 = t4 + toT(seg.release);                               // end of release
    const t6 = 1;

    const pts = [
      [t0, depthDb],
      [t1, depthDb],
      [t2, 0],
      [t3, 0],
      [t4, 0],
      [t5, depthDb],
      [t6, depthDb],
    ];

    // Shaded closed region
    if (params.depth > 0) {
      ctx.fillStyle = 'rgba(255,80,80,0.04)';
      ctx.fillRect(margin.left, depthY, plotW, openY - depthY);
    }

    // Filled area under envelope
    ctx.beginPath();
    ctx.moveTo(timeToX(pts[0][0]), gainToY(pts[0][1]));
    for (const [t, g] of pts) ctx.lineTo(timeToX(t), gainToY(g));
    ctx.lineTo(timeToX(1), gainToY(gainMin));
    ctx.lineTo(timeToX(0), gainToY(gainMin));
    ctx.closePath();
    ctx.fillStyle = 'rgba(240,192,64,0.08)';
    ctx.fill();

    // Envelope curve
    ctx.beginPath();
    ctx.moveTo(timeToX(pts[0][0]), gainToY(pts[0][1]));
    for (const [t, g] of pts) ctx.lineTo(timeToX(t), gainToY(g));
    ctx.strokeStyle = '#f0c040';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Phase labels
    ctx.fillStyle = 'rgba(240,192,64,0.5)';
    ctx.font = '8px monospace';
    ctx.textAlign = 'center';
    if (t2 - t1 > 0.03) ctx.fillText('ATK', timeToX((t1 + t2) / 2), margin.top + 9);
    if (t4 - t3 > 0.03) ctx.fillText('HOLD', timeToX((t3 + t4) / 2), margin.top + 9);
    if (t5 - t4 > 0.04) ctx.fillText('REL', timeToX((t4 + t5) / 2), margin.top + 9);

    // Depth label
    if (params.depth > 0.005) {
      ctx.fillStyle = 'rgba(230,100,100,0.75)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      const labelY = Math.min(H - 6, depthY - 4);
      ctx.fillText('DEPTH: ' + depthDb.toFixed(0) + ' dB', margin.left + 4, labelY);
    }
  }, [params]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={160}
      style={{ width: '100%', height: '160px', display: 'block', borderRadius: '4px', border: '1px solid #2a2a2a' }}
    />
  );
}

// ---- localStorage Helpers ----
const LS_KEY = 'uisnapshot_gate_presets';
function loadPresets() {
  try { const r = localStorage.getItem(LS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function persistPresets(p) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ---- Main Gate Dialog Component ----
export default function GateDialog({ open, onClose, read, write, channelName }) {
  const pf = (key, fb) => { const v = parseFloat(read(key)); return isNaN(v) ? fb : v; };

  const [bypass, setBypass] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [prname, setPrname] = useState('');

  const [thresh, setThresh] = useState(0.1);
  const [attack, setAttack] = useState(0);
  const [hold, setHold] = useState(0.3);
  const [release, setRelease] = useState(0.3);
  const [depth, setDepth] = useState(0);
  const [prmod, setPrmod] = useState(0);

  const [presets, setPresets] = useState(loadPresets);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Load values from snapshot when dialog opens
  useEffect(() => {
    if (!open) return;
    setBypass(String(read('gate.bypass')) === '1');
    setEnabled(String(read('gate.enabled')) !== '0');
    const name = read('gate.prname') || '';
    setPrname(name);
    setSelectedPreset(name);

    setThresh(pf('gate.thresh', 0.1));
    setAttack(pf('gate.attack', 0));
    setHold(pf('gate.hold', 0.3));
    setRelease(pf('gate.release', 0.3));
    setDepth(pf('gate.depth', 0));
    setPrmod(pf('gate.prmod', 0));
  }, [open]); // intentionally only re-run when dialog opens/closes

  // Clear preset name when a parameter is changed manually
  const writeParam = useCallback((key, value) => {
    write(key, value);
    write('gate.prname', '');
    setPrname('');
    setSelectedPreset('');
  }, [write]);

  const gateParams = { bypass, enabled, thresh, attack, hold, release, depth, prmod };

  // ---- Preset helpers ----
  function applyPresetParams(p, presetName) {
    const parseF = (key, fb) => { const v = parseFloat(p[key]); return isNaN(v) ? fb : v; };
    const newBypass = String(p['gate.bypass']) === '1';
    const newEnabled = String(p['gate.enabled']) !== '0';

    setBypass(newBypass); write('gate.bypass', newBypass ? 1 : 0);
    setEnabled(newEnabled); write('gate.enabled', newEnabled ? 1 : 0);

    const newThresh = parseF('gate.thresh', 0.1);
    setThresh(newThresh); write('gate.thresh', newThresh);

    const newAttack = parseF('gate.attack', 0);
    setAttack(newAttack); write('gate.attack', newAttack);

    const newHold = parseF('gate.hold', 0.3);
    setHold(newHold); write('gate.hold', newHold);

    const newRelease = parseF('gate.release', 0.3);
    setRelease(newRelease); write('gate.release', newRelease);

    const newDepth = parseF('gate.depth', 0);
    setDepth(newDepth); write('gate.depth', newDepth);

    const newPrmod = parseF('gate.prmod', 0);
    setPrmod(newPrmod); write('gate.prmod', newPrmod);

    write('gate.prname', presetName);
    setPrname(presetName);
    setSelectedPreset(presetName);
  }

  function handlePresetSelect(name) {
    setSelectedPreset(name);
    if (!name) return;
    const preset = presets.find((p) => p.name === name);
    if (preset) applyPresetParams(preset.params, name);
  }

  function handleSavePreset() {
    const name = window.prompt('Enter preset name:', prname || '');
    if (!name) return;
    const preset = {
      name,
      params: {
        'gate.bypass': bypass ? '1' : '0',
        'gate.enabled': enabled ? '1' : '0',
        'gate.thresh': String(thresh),
        'gate.attack': String(attack),
        'gate.hold': String(hold),
        'gate.release': String(release),
        'gate.depth': String(depth),
        'gate.prmod': String(prmod),
      },
    };
    const updated = [...presets.filter((p) => p.name !== name), preset];
    setPresets(updated);
    persistPresets(updated);
    write('gate.prname', name);
    setPrname(name);
    setSelectedPreset(name);
  }

  function handleExportPresets() {
    const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gate-presets.json'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportPresets() {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target.result);
          if (!Array.isArray(imported)) throw new Error('not array');
          const merged = [...presets];
          let count = 0;
          for (const p of imported) {
            if (p && p.name && p.params) {
              const idx = merged.findIndex((x) => x.name === p.name);
              if (idx >= 0) merged[idx] = p; else merged.push(p);
              count++;
            }
          }
          setPresets(merged); persistPresets(merged);
          window.alert('Imported ' + count + ' preset(s).');
        } catch { window.alert('Failed to import: expected a JSON array of preset objects with name and params fields.'); }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="eq-overlay" onClick={onClose}>
      <div className="eq-dialog" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="eq-header">
          <span className="eq-title">GATE — {channelName || 'Channel'}</span>
          <div className="eq-header-right">
            <label className="eq-toggle">
              <input
                type="checkbox" checked={enabled}
                onChange={(e) => { const v = e.target.checked; setEnabled(v); write('gate.enabled', v ? 1 : 0); }}
              />
              <span>ENABLED</span>
            </label>
            <label className="eq-toggle">
              <input
                type="checkbox" checked={bypass}
                onChange={(e) => { const v = e.target.checked; setBypass(v); write('gate.bypass', v ? 1 : 0); }}
              />
              <span>BYPASS</span>
            </label>
            {prname && <span className="eq-active-preset" title={'Preset: ' + prname}>⬡ {prname}</span>}
            <button className="eq-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Gate envelope visualization */}
        <div className="eq-canvas-wrap">
          <GateCanvas params={gateParams} />
        </div>

        {/* Parameter controls */}
        <div className="gate-params">

          {/* Threshold */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#f0c040' }}>THRESHOLD</div>
            <div className="gate-param-desc">Signal level below which the gate closes</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={thresh}
              onChange={(e) => { const v = parseFloat(e.target.value); setThresh(v); writeParam('gate.thresh', v); }}
              className="eq-slider"
            />
            <div className="eq-value">{formatThresh(thresh)}</div>
          </div>

          {/* Attack */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#7ec8e3' }}>ATTACK</div>
            <div className="gate-param-desc">Time to fully open after signal exceeds threshold</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={attack}
              onChange={(e) => { const v = parseFloat(e.target.value); setAttack(v); writeParam('gate.attack', v); }}
              className="eq-slider gate-slider-attack"
            />
            <div className="eq-value">{formatMs(normToMs(attack, ATTACK_MAX_MS))}</div>
          </div>

          {/* Hold */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#a0d8a0' }}>HOLD</div>
            <div className="gate-param-desc">Time gate stays open after signal drops below threshold</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={hold}
              onChange={(e) => { const v = parseFloat(e.target.value); setHold(v); writeParam('gate.hold', v); }}
              className="eq-slider gate-slider-hold"
            />
            <div className="eq-value">{formatMs(normToMs(hold, HOLD_MAX_MS))}</div>
          </div>

          {/* Release */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#e8a0e8' }}>RELEASE</div>
            <div className="gate-param-desc">Time to fully close after the hold period ends</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={release}
              onChange={(e) => { const v = parseFloat(e.target.value); setRelease(v); writeParam('gate.release', v); }}
              className="eq-slider gate-slider-release"
            />
            <div className="eq-value">{formatMs(normToMs(release, RELEASE_MAX_MS))}</div>
          </div>

          {/* Depth */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#f0a070' }}>DEPTH</div>
            <div className="gate-param-desc">Attenuation applied when the gate is closed</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={depth}
              onChange={(e) => { const v = parseFloat(e.target.value); setDepth(v); writeParam('gate.depth', v); }}
              className="eq-slider gate-slider-depth"
            />
            <div className="eq-value">{formatDepth(depth)}</div>
          </div>

          {/* Mode (prmod) */}
          <div className="gate-param-card">
            <div className="gate-param-title" style={{ color: '#aaaaaa' }}>MODE</div>
            <div className="gate-param-desc">Processing mode affecting gate detection behavior</div>
            <input
              type="range" min={0} max={1} step={0.001}
              value={prmod}
              onChange={(e) => { const v = parseFloat(e.target.value); setPrmod(v); writeParam('gate.prmod', v); }}
              className="eq-slider gate-slider-mode"
            />
            <div className="eq-value">{prmod.toFixed(3)}</div>
          </div>

        </div>

        {/* Preset bar */}
        <div className="eq-preset-bar">
          <select
            className="eq-preset-select"
            value={selectedPreset}
            onChange={(e) => handlePresetSelect(e.target.value)}
          >
            <option value="">— Select Preset —</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          <button className="eq-btn" onClick={handleSavePreset}>SAVE</button>
          <button className="eq-btn" onClick={handleExportPresets}>EXPORT</button>
          <button className="eq-btn" onClick={handleImportPresets}>IMPORT</button>
        </div>

      </div>
    </div>,
    document.body
  );
}
